import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceService, purchaseService } from '../../services/financeService';
import './Financial.css';

const fmt = (n) => n != null ? Number(n).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '—';

// One {fg, bg} pair per status (covers both Invoice and Purchase statuses — no duplicate keys,
// and using the actual light-tint tokens instead of string-concatenating "20" onto a CSS var,
// which silently produced invalid CSS and dropped the badge's background entirely).
const STATUS_STYLE = {
  DRAFT: { fg: 'var(--color-text-tertiary)', bg: 'var(--color-neutral-100)' },
  SENT: { fg: 'var(--color-primary)', bg: 'var(--color-neutral-100)' },
  PAID: { fg: 'var(--color-success)', bg: 'var(--color-success-light)' },
  PARTIAL: { fg: 'var(--color-warning)', bg: 'var(--color-warning-light)' },
  OVERDUE: { fg: 'var(--color-error)', bg: 'var(--color-error-light)' },
  CANCELLED: { fg: 'var(--color-text-secondary)', bg: 'var(--color-neutral-100)' },
  UNPAID: { fg: 'var(--color-error)', bg: 'var(--color-error-light)' },
};

const badge = (status) => {
  const style = STATUS_STYLE[status] || STATUS_STYLE.DRAFT;
  return (
    <span style={{
      background: style.bg,
      color: style.fg,
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 600
    }}>
      {status}
    </span>
  );
};

export default function SalesPurchasesSummary() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sales');
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [salesType, setSalesType] = useState(''); // LOCAL, EXPORT

  // Cancel modal state
  const [cancelTarget, setCancelTarget] = useState(null); // { id, type: 'sale'|'purchase', label }
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const openCancel = (id, type, label) => { setCancelTarget({ id, type, label }); setCancelReason(''); };
  const closeCancel = () => { setCancelTarget(null); setCancelReason(''); };

  const doCancel = async () => {
    if (!cancelReason.trim()) return;
    setCancelLoading(true);
    try {
      if (cancelTarget.type === 'purchase') {
        await purchaseService.cancel(cancelTarget.id, cancelReason);
      } else {
        await invoiceService.cancel(cancelTarget.id, cancelReason);
      }
      showToast(`${cancelTarget.type === 'purchase' ? 'Purchase' : 'Invoice'} cancelled`);
      closeCancel();
      loadData();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setCancelLoading(false);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [invRes, purList] = await Promise.all([
        invoiceService.getAll(),
        purchaseService.getAll(),
      ]);
      // Filter to only INVOICE type (not PROFORMA, QUOTE, CREDIT_NOTE)
      const invoices = (invRes?.data || invRes || []).filter(i => i.invoiceType === 'INVOICE');
      setSales(invoices);
      setPurchases(Array.isArray(purList) ? purList : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Apply filters
  const filteredSales = sales.filter(s => {
    if (dateFrom && s.invoiceDate < dateFrom) return false;
    if (dateTo && s.invoiceDate > dateTo) return false;
    if (salesType && s.salesType !== salesType) return false;
    return true;
  });

  const filteredPurchases = purchases.filter(p => {
    if (dateFrom && p.purchaseDate < dateFrom) return false;
    if (dateTo && p.purchaseDate > dateTo) return false;
    return true;
  });

  // Summaries — keep PKR and USD separate to avoid mixing currencies
  const totalSalesPKR = filteredSales
    .filter(s => s.currency !== 'USD' && s.status !== 'CANCELLED')
    .reduce((sum, s) => sum + (parseFloat(s.totalAmount) || 0), 0);
  const totalSalesUSD = filteredSales
    .filter(s => s.currency === 'USD' && s.status !== 'CANCELLED')
    .reduce((sum, s) => sum + (parseFloat(s.totalAmount) || 0), 0);
  const totalPurchasesPKR = filteredPurchases
    .filter(p => p.currency !== 'USD' && p.status !== 'CANCELLED')
    .reduce((sum, p) => sum + (parseFloat(p.totalAmount) || 0), 0);
  const totalPurchasesUSD = filteredPurchases
    .filter(p => p.currency === 'USD' && p.status !== 'CANCELLED')
    .reduce((sum, p) => sum + (parseFloat(p.totalAmount) || 0), 0);
  const totalPurchasesPaidPKR = filteredPurchases
    .filter(p => p.currency !== 'USD' && p.status !== 'CANCELLED')
    .reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);
  const totalPurchasesPaidUSD = filteredPurchases
    .filter(p => p.currency === 'USD' && p.status !== 'CANCELLED')
    .reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);

  return (
    <div className="financial-container">
      <div className="financial-header">
        <div>
          <h2 className="financial-title">Sales & Purchases</h2>
          <p className="financial-subtitle">View all sales (invoices) and purchases in one place</p>
        </div>
      </div>

      {error && (
        <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-content">
            <span className="metric-label">Total Sales (PKR)</span>
            <span className="metric-value">PKR {fmt(totalSalesPKR)}</span>
          </div>
        </div>
        {totalSalesUSD > 0 && (
          <div className="metric-card">
            <div className="metric-content">
              <span className="metric-label">Total Sales (USD)</span>
              <span className="metric-value">USD {fmt(totalSalesUSD)}</span>
            </div>
          </div>
        )}
        <div className="metric-card">
          <div className="metric-content">
            <span className="metric-label">Total Purchases (PKR)</span>
            <span className="metric-value">PKR {fmt(totalPurchasesPKR)}</span>
            {totalPurchasesUSD > 0 && <span className="metric-sub">+ USD {fmt(totalPurchasesUSD)}</span>}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-content">
            <span className="metric-label">Purchases Unpaid (PKR)</span>
            <span className="metric-value" style={{ color: (totalPurchasesPKR - totalPurchasesPaidPKR) > 0 ? 'var(--color-error)' : undefined }}>
              PKR {fmt(totalPurchasesPKR - totalPurchasesPaidPKR)}
            </span>
            {totalPurchasesUSD > 0 && <span className="metric-sub">+ USD {fmt(totalPurchasesUSD - totalPurchasesPaidUSD)}</span>}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="date" className="form-control" style={{ width: '150px' }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" />
        <span style={{ color: 'var(--color-text-tertiary)' }}>to</span>
        <input type="date" className="form-control" style={{ width: '150px' }} value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To" />
        {activeTab === 'sales' && (
          <select className="form-control" style={{ width: '140px' }} value={salesType} onChange={e => setSalesType(e.target.value)}>
            <option value="">All Types</option>
            <option value="LOCAL">Local</option>
            <option value="EXPORT">Export</option>
          </select>
        )}
        {(dateFrom || dateTo || salesType) && (
          <button className="btn-secondary" onClick={() => { setDateFrom(''); setDateTo(''); setSalesType(''); }}>Clear</button>
        )}
      </div>

      {/* Tabs — same underline style as every other page's tab-container */}
      <div className="tab-container">
        <button className={`tab-button ${activeTab === 'sales' ? 'active' : ''}`} onClick={() => setActiveTab('sales')}>
          Sales ({filteredSales.length})
        </button>
        <button className={`tab-button ${activeTab === 'purchases' ? 'active' : ''}`} onClick={() => setActiveTab('purchases')}>
          Purchases ({filteredPurchases.length})
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading...</div>
      ) : (
        <>
          {/* Sales Tab */}
          {activeTab === 'sales' && (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Type</th>
                    <th>Currency</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'right' }}>Paid</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.length === 0 ? (
                    <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: '32px' }}>No sales found</td></tr>
                  ) : filteredSales.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600, color: 'var(--color-primary)' }}>{s.invoiceNumber}</td>
                      <td>{s.invoiceDate}</td>
                      <td>{s.customerName}</td>
                      <td>
                        <span style={{
                          background: s.salesType === 'EXPORT' ? 'var(--color-info-light)' : 'var(--color-neutral-100)',
                          color: s.salesType === 'EXPORT' ? 'var(--color-info)' : 'var(--color-text-secondary)',
                          padding: '2px 8px', borderRadius: '4px', fontSize: '11px'
                        }}>
                          {s.salesType || 'LOCAL'}
                        </span>
                      </td>
                      <td>{s.currency}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {s.currency === 'USD' ? 'USD ' : 'PKR '}{fmt(s.totalAmount)}
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--color-success)' }}>
                        {s.currency === 'USD' ? 'USD ' : 'PKR '}{fmt(s.amountPaid || 0)}
                      </td>
                      <td>
                        {badge(s.status)}
                        {s.status === 'CANCELLED' && s.cancelReason && (
                          <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '3px', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.cancelReason}>{s.cancelReason}</div>
                        )}
                      </td>
                      <td style={{ display: 'flex', gap: '6px' }}>
                        <button className="btn-secondary" style={{ padding: '3px 10px', fontSize: '12px' }} onClick={() => navigate('/invoices')}>View</button>
                        {s.status !== 'CANCELLED' && s.status !== 'PAID' && (
                          <button className="btn-danger" style={{ padding: '3px 10px', fontSize: '12px' }} onClick={() => openCancel(s.id, 'sale', s.invoiceNumber)}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Purchases Tab */}
          {activeTab === 'purchases' && (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>PO #</th>
                    <th>Date</th>
                    <th>Vendor</th>
                    <th>Currency</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'right' }}>Paid</th>
                    <th style={{ textAlign: 'right' }}>Outstanding</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPurchases.length === 0 ? (
                    <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: '32px' }}>No purchases found</td></tr>
                  ) : filteredPurchases.map(p => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600, color: 'var(--color-error)' }}>{p.purchaseNumber}</td>
                      <td>{p.purchaseDate}</td>
                      <td>{p.vendorName}</td>
                      <td>{p.currency}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {p.currency === 'USD' ? 'USD ' : 'PKR '}{fmt(p.totalAmount)}
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--color-success)' }}>
                        {p.currency === 'USD' ? 'USD ' : 'PKR '}{fmt(p.paidAmount || 0)}
                      </td>
                      <td style={{ textAlign: 'right', color: parseFloat(p.outstandingAmount) > 0 ? 'var(--color-error)' : 'var(--color-success)', fontWeight: 600 }}>
                        {p.currency === 'USD' ? 'USD ' : 'PKR '}{fmt(p.outstandingAmount || 0)}
                      </td>
                      <td>
                        {badge(p.status)}
                        {p.status === 'CANCELLED' && p.cancelReason && (
                          <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '3px', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.cancelReason}>{p.cancelReason}</div>
                        )}
                      </td>
                      <td style={{ display: 'flex', gap: '6px' }}>
                        <button className="btn-secondary" style={{ padding: '3px 10px', fontSize: '12px' }} onClick={() => navigate('/purchases')}>View</button>
                        {p.status !== 'CANCELLED' && (
                          <button className="btn-danger" style={{ padding: '3px 10px', fontSize: '12px' }} onClick={() => openCancel(p.id, 'purchase', p.purchaseNumber)}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {/* Cancel modal */}
      {cancelTarget && (
        <div className="modal-overlay" onClick={closeCancel}>
          <div className="modal-content" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel {cancelTarget.type === 'purchase' ? 'Purchase' : 'Invoice'}</h3>
              <button className="modal-close" onClick={closeCancel}>✕</button>
            </div>
            <div style={{ padding: '4px 0 16px' }}>
              <p style={{ color: 'var(--color-text-secondary)', marginBottom: '14px' }}>
                Cancelling <strong>{cancelTarget.label}</strong>.
                {cancelTarget.type === 'purchase' && ' Stock will be reversed.'}
                {' '}Please provide a reason.
              </p>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: '6px' }}>
                Cancel Reason *
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="e.g. Duplicate entry, wrong vendor, customer request..."
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                autoFocus
                style={{ resize: 'vertical' }}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" disabled={cancelLoading} onClick={closeCancel}>No, keep it</button>
              <button className="btn-danger" disabled={!cancelReason.trim() || cancelLoading} onClick={doCancel}>
                {cancelLoading ? 'Cancelling…' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          background: toast.type === 'error' ? 'var(--color-error-light)' : 'var(--color-success-light)',
          color: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
          border: `1px solid ${toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)'}`,
          padding: '14px 22px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
